import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, Badge, Input, Skeleton } from "antd";
import { SearchOutlined, UserOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AppShell from "../../components/app-shell/AppShell";
import useUser from "../../hooks/useUser";
import { getChatSocket } from "../../lib/chatSocket";
import "./member.css";

// Today's chats show the time, older ones the date, like a phone's message list.
const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const sameDay = date.toDateString() === new Date().toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const Messages = ({ endUser }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getConversations, getCommunityMembers, upsertConversation } = useUser();
  const [search, setSearch] = useState("");

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => getConversations({ page: 1, limit: 50 }),
    refetchOnWindowFocus: true,
    refetchInterval: 15000,
  });

  const { data: memberSearchData } = useQuery({
    queryKey: ["chat-member-search", search],
    queryFn: () => getCommunityMembers({ q: search, page: 1, limit: 8, sortBy: "name-asc" }),
    enabled: String(search || "").trim().length >= 2,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const { mutate: startConversationAction, isPending: startingConversation } = useMutation({
    mutationFn: upsertConversation,
    onSuccess: (conversation) => {
      if (!conversation?._id) return;
      navigate(`/messages/${conversation._id}`);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || "Could not start conversation");
    },
  });

  useEffect(() => {
    const socket = getChatSocket();
    const onNewMessage = () => queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    const onRead = () => queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
    socket.on("chat:new_message", onNewMessage);
    socket.on("chat:messages_read", onRead);
    return () => {
      socket.off("chat:new_message", onNewMessage);
      socket.off("chat:messages_read", onRead);
    };
  }, [queryClient]);

  const searching = String(search || "").trim().length >= 2;
  const searchedMembers = (memberSearchData?.users || []).filter(
    (m) => String(m._id) !== String(endUser?._id)
  );

  return (
    <AppShell title="Chats">
      <Input
        className="chat-search app-search"
        size="large"
        allowClear
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search members to start a chat"
        prefix={<SearchOutlined />}
      />

      {searching ? (
        <>
          <div className="app-section-title">Members</div>
          {searchedMembers.length === 0 ? (
            <div className="app-empty">No member found</div>
          ) : (
            <div className="app-list">
              {searchedMembers.map((member) => (
                <button
                  type="button"
                  key={member._id}
                  className="app-list-item"
                  disabled={startingConversation}
                  onClick={() => startConversationAction({ participantId: member._id })}
                >
                  <Avatar size={44} src={member?.profilePhoto || undefined} icon={<UserOutlined />} />
                  <span className="app-list-item__main">
                    <span className="app-list-item__title">{member?.name || "Member"}</span>
                    <span className="app-list-item__sub">
                      {[member?.upuzila, member?.district].filter(Boolean).join(", ") || "Tap to chat"}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          <div className="app-section-title">Recent chats</div>
        </>
      ) : null}

      {isLoading ? (
        <div className="app-list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="app-list-item">
              <Skeleton avatar active title={false} paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="app-empty">No conversations yet. Search for a member above to say hi.</div>
      ) : (
        <div className="app-list">
          {conversations.map((conv) => {
            const p = conv?.otherParticipant || {};
            const unread = conv?.unreadCount || 0;
            return (
              <button
                type="button"
                key={conv._id}
                className="app-list-item"
                onClick={() => navigate(`/messages/${conv._id}`)}
              >
                <Avatar size={48} src={p?.profilePhoto || undefined} icon={<UserOutlined />} />
                <span className="app-list-item__main">
                  <span className="app-list-item__title">{p?.name || "Member"}</span>
                  <span className="app-list-item__sub">
                    {conv?.lastMessage?.text || (unread > 0 ? `${unread} new messages` : "Tap to open chat")}
                  </span>
                </span>
                <span className="app-list-item__end">
                  {formatDate(conv?.lastMessage?.createdAt || conv?.updatedAt)}
                  {unread > 0 ? (
                    <Badge count={unread} size="small" style={{ display: "block", marginTop: 4 }} />
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </AppShell>
  );
};

export default Messages;
